import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrency, type CurrencyCode } from "@majourneys/currency";
import { useSession } from "../../../lib/auth";
import { profileApi } from "../api/profile.api";
import { accountKeys } from "../constants/query-keys";
import { useMyProfile } from "./use-my-profile";

/**
 * Bridges the account's stored preferences and the ones the browser holds.
 *
 * They used to be two settings wearing one name. The header switcher wrote
 * currency to localStorage and every price on the site read only that; the
 * Preferences tab wrote to `users.currency` and nothing ever read it. Setting
 * EUR in Preferences changed no price, setting it in the header left
 * Preferences saying MAD, and the Preferences copy — "used to show every price
 * you see" — was simply untrue. Language was worse: the dropdown navigated to
 * /fr/… and never saved anything at all.
 *
 * The rule: the pick a guest makes on this device wins, and is saved to the
 * account. The account's value only seeds a device that has no pick of its own
 * yet (a fresh laptop after signing in). It used to be "the account wins" on
 * every page load, so a signed-in guest who switched to USD in the header was
 * flipped back to the account's EUR on the next page (traveller test,
 * 2026-11-01). Signed out, nothing here does anything and the browser store is
 * the whole story.
 */

/** Currency the account remembers, or null if it holds nothing usable. */
function accountCurrency(value: string | undefined): CurrencyCode | null {
  return value === "MAD" || value === "EUR" || value === "USD" || value === "GBP" ? value : null;
}

/**
 * Mount once, high up. On sign-in, either seeds this device from the account
 * (no pick here yet: a guest who picked EUR on their phone sees EUR on a new
 * laptop) or, when this device already has a pick, saves that pick to the
 * account so the two agree.
 */
export function useApplyAccountPreferences() {
  const { data: session } = useSession();
  const signedIn = Boolean(session?.user);
  const profile = useMyProfile({ enabled: signedIn });
  const { currency, setCurrency, explicit, resolved } = useCurrency();
  const { save } = useSavePreferences();
  const applied = useRef(false);

  useEffect(() => {
    if (!signedIn) {
      // Signing out hands the browser its own choice back.
      applied.current = false;
      return;
    }
    // Wait until the browser has read its own stored pick, or a guest's pick
    // would look like "no pick" for one render and be overwritten.
    if (applied.current || !resolved || !profile.data) return;
    applied.current = true;
    const stored = accountCurrency(profile.data.preferences.currency);
    if (explicit) {
      if (stored !== currency) void save({ currency });
      return;
    }
    if (stored && stored !== currency) setCurrency(stored);
  }, [signedIn, resolved, explicit, profile.data, currency, setCurrency, save]);
}

/**
 * Writes a change back to the account. Safe to call when signed out — it does
 * nothing — so callers never have to branch on the session.
 */
export function useSavePreferences() {
  const { data: session } = useSession();
  const signedIn = Boolean(session?.user);
  const queryClient = useQueryClient();

  const save = async (patch: { currency?: string; language?: string; location?: string }) => {
    if (!signedIn) return;
    // Fetch rather than read off state: the header switchers are usable the
    // moment the page paints, well before the profile query settles, and
    // reading here would silently drop that first change. ensureQueryData
    // hands back the cached copy when there is one, so this is normally free.
    let prefs;
    try {
      prefs = (
        await queryClient.ensureQueryData({
          queryKey: accountKeys.profile(),
          queryFn: profileApi.getMine,
        })
      ).preferences;
    } catch {
      return;
    }
    if (!prefs) return;
    // Nothing to do — and skipping the write keeps this out of a loop with
    // useApplyAccountPreferences, which reacts to the same query.
    if (Object.entries(patch).every(([k, v]) => prefs[k as keyof typeof prefs] === v)) return;
    try {
      await profileApi.updatePreferences({ ...prefs, ...patch });
      void queryClient.invalidateQueries({ queryKey: accountKeys.profile() });
    } catch {
      // A preference that fails to save is not worth interrupting anyone over;
      // the browser still has the change and the next save will carry it.
    }
  };

  return { save, signedIn };
}
